import { Bell, Clipboard, Mail, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Linking, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { DigestData } from '@/lib/digest';
import {
  WEB_NOTIFICATIONS_AVAILABLE,
  showImmediateWebNotification,
} from '@/lib/notifications';

/**
 * Preview-and-send sheet for the weekly digest. The bare mailto: link is
 * unreliable — on web many browsers have no mail handler and the click
 * silently does nothing. This sheet always shows the digest content, lets
 * the user Copy to clipboard (works everywhere), and offers Open in email
 * as a secondary action.
 */
export function DigestPreview({

  digest,
  onClose,
}: {
  digest: DigestData | null;
  onClose: () => void;
}) {
  const insets = useSafeAreaInsets();
  const open = digest != null;
  const anim = useSharedValue(0);
  const [copied, setCopied] = useState(false);
  const [pinged, setPinged] = useState(false);

  useEffect(() => {
    anim.value = withTiming(open ? 1 : 0, {
      duration: 220,
      easing: Easing.out(Easing.cubic),
    });
    if (!open) {
      setCopied(false);
      setPinged(false);
    }
  }, [open, anim]);

  const sheetStyle = useAnimatedStyle(() => ({
    opacity: anim.value,
    transform: [{ translateY: (1 - anim.value) * 40 }],
  }));
  const backdropStyle = useAnimatedStyle(() => ({ opacity: anim.value * 0.55 }));

  if (!digest) return null;

  const copyToClipboard = async () => {
    const text = `${digest.subject}\n\n${digest.body}`;
    try {
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-undef
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          // eslint-disable-next-line no-undef
          await navigator.clipboard.writeText(text);
        }
      } else {
        // expo-clipboard isn't installed; fall back to RN-Web style via Linking
        // copy not available on native without expo-clipboard. We just open mail.
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const sendBrowserPing = async () => {
    if (!digest) return;
    // Use the first line as the body — keeps the notification scannable.
    const firstLine = digest.body.split('\n').find((l) => l.trim().length > 0) ?? '';
    const ok = await showImmediateWebNotification(digest.subject, firstLine);
    if (ok) {
      setPinged(true);
      setTimeout(() => setPinged(false), 2200);
    }
  };

  const openMail = async () => {
    const url = `mailto:?subject=${encodeURIComponent(digest.subject)}&body=${encodeURIComponent(digest.body)}`;
    try {
      await Linking.openURL(url);
    } catch {
      // No mail handler available — copy is still useful.
    }
  };

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={onClose}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={styles.backdropPress} onPress={onClose} />
      </Animated.View>
      <View pointerEvents="box-none" style={styles.wrap}>
        <Animated.View style={[styles.sheet, { paddingBottom: 20 + insets.bottom }, sheetStyle]}>
          <View style={styles.head}>
            <View style={{ flex: 1 }}>
              <Text style={styles.eyebrow}>WEEKLY DIGEST</Text>
              <Text style={styles.title} numberOfLines={1}>{digest.subject}</Text>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={10}
              style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <ScrollView style={styles.bodyScroll} contentContainerStyle={styles.bodyContent}>
            <Text style={styles.body}>{digest.body}</Text>
          </ScrollView>

          <View style={styles.actions}>
            <Pressable
              onPress={copyToClipboard}
              style={({ pressed }) => [
                styles.btnSecondary,
                pressed && { opacity: 0.85 },
              ]}>
              <Clipboard size={16} color={Palette.forest} strokeWidth={1.8} />
              <Text style={styles.btnSecondaryText}>
                {copied ? 'Copied!' : 'Copy text'}
              </Text>
            </Pressable>
            <Pressable
              onPress={openMail}
              style={({ pressed }) => [
                styles.btnPrimary,
                pressed && { transform: [{ scale: 0.97 }] },
              ]}>
              <Mail size={16} color={Palette.cream} strokeWidth={2} />
              <Text style={styles.btnPrimaryText}>Open in email</Text>
            </Pressable>
          </View>
          {WEB_NOTIFICATIONS_AVAILABLE ? (
            <Pressable
              onPress={sendBrowserPing}
              style={({ pressed }) => [
                styles.btnTertiary,
                pressed && { opacity: 0.85 },
              ]}>
              <Bell size={14} color={Palette.honeyDeep} strokeWidth={2} />
              <Text style={styles.btnTertiaryText}>
                {pinged ? 'Sent — check your browser' : 'Send to this browser as a notification'}
              </Text>
            </Pressable>
          ) : null}
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 24, 18, 1)',
  },
  backdropPress: { flex: 1 },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    maxHeight: '85%',
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: 24,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 6,
  },
  eyebrow: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.honeyDeep,
    letterSpacing: 0.8,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 17,
    color: Palette.ink,
    letterSpacing: -0.3,
    marginTop: 2,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  bodyScroll: {
    marginTop: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    maxHeight: 320,
  },
  bodyContent: {
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  body: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    lineHeight: 20,
    color: Palette.ink,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: Spacing.three,
  },
  btnSecondary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 13,
    borderRadius: 14,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
  },
  btnSecondaryText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.forest,
  },
  btnPrimary: {
    flex: 1.4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 13,
    borderRadius: 14,
    backgroundColor: Palette.forest,
  },
  btnPrimaryText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.cream,
  },
  btnTertiary: {
    marginTop: 8,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 10, borderRadius: 12,
    backgroundColor: 'rgba(225,163,61,0.12)',
    borderWidth: 1, borderColor: 'rgba(225,163,61,0.3)',
  },
  btnTertiaryText: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.honeyDeep,
  },
});
